import type { Record } from "@suse-o11y/record";
import { useEffect, useMemo } from "react";

import type { Monaco } from "../_monaco";

const DEFAULT_OPTIONS = {
	stickyScroll: {
		enabled: false,
	},
	// Ensures marker popups/tooltips are not trimmed by overflowing the editor
	fixedOverflowWidgets: true,
	minimap: {
		// Hide the minimap
		enabled: false,
	},
	scrollbar: {
		// Prevent none scrollable editor from capturing the container's scroll events
		// https://github.com/kubernetes-sigs/kui/issues/6480#issuecomment-1493085164
		alwaysConsumeMouseWheel: false,
	},
	// do not insert the space after the last line
	scrollBeyondLastLine: false,
} satisfies Monaco.editor.IStandaloneEditorConstructionOptions;

/**
 * https://farzadyz.me/blog/single-line-monaco-editor
 */
const INPUT_AND_TEXTAREA_OPTIONS = {
	contextmenu: false,
	roundedSelection: false,
	cursorStyle: "line-thin",
	wordWrap: "off",
	lineNumbers: "off",
	folding: false,
	lineNumbersMinChars: 0,
	renderLineHighlight: "none",
	occurrencesHighlight: "off",
	links: false,
	overviewRulerLanes: 0,
	overviewRulerBorder: false,
	scrollbar: DEFAULT_OPTIONS.scrollbar,
	// disable `Find`
	find: {
		addExtraSpaceOnTop: false,
		autoFindInSelection: "never",
		seedSearchStringFromSelection: "never",
	},
	guides: {
		indentation: false,
	},
} satisfies Monaco.editor.IStandaloneEditorConstructionOptions;

const INPUT_OPTIONS = {
	...INPUT_AND_TEXTAREA_OPTIONS,
	scrollbar: {
		...INPUT_AND_TEXTAREA_OPTIONS.scrollbar,
		vertical: "hidden",
		horizontal: "hidden",
	},
} satisfies Monaco.editor.IStandaloneEditorConstructionOptions;

const TEXTAREA_OPTIONS = {
	...INPUT_AND_TEXTAREA_OPTIONS,
	wordWrap: "on",
} satisfies Monaco.editor.IStandaloneEditorConstructionOptions;

const CODE_OPTIONS = {
	wordWrap: "on",
} satisfies Monaco.editor.IStandaloneEditorConstructionOptions;

type EditorSize = "sm" | "md" | "lg";

const SIZE_TO_CONFIG = {
	sm: [12, 2, 8],
	md: [13, 5, 12, 3],
	lg: [14, 8, 16, 7],
} satisfies Record<EditorSize, [font: number, topPadding: number, xPadding: number, bottomPadding?: number]>;

export type EditorAppearance = "code" | "input" | "textarea";

export const DEFAULT_EDITOR_APPEARANCE = "code" satisfies EditorAppearance;

export interface EditorOptions
	extends Pick<Monaco.editor.IStandaloneEditorConstructionOptions, "readOnly" | "placeholder"> {
	quickSuggestions: boolean;

	/**
	 * @default "md"
	 */
	size?: EditorSize;

	/**
	 * @default false
	 */
	disableLineNumbers?: boolean;

	/**
	 * @default false
	 */
	disableRuler?: boolean;

	/**
	 * @default false
	 */
	disableLineHighlight?: boolean;

	/**
	 * @default false
	 */
	disableLinks?: boolean;

	/**
	 * When set to "input", makes the editor behave like a single-line input field (disables line breaks and enter key inserts)
	 * When set to "textarea", makes the editor behave like a textarea field
	 * When set to "code", makes the editor behave like a code editor with syntax highlighting, line numbers, word wrapping, etc.
	 *
	 * @default "code"
	 */
	appearance?: EditorAppearance;
	inDiffEditor?: boolean;
	glyphMargin?: boolean;
}

export function useEditorOptions(
	editor: null | Monaco.editor.ICodeEditor,
	{
		appearance = DEFAULT_EDITOR_APPEARANCE,
		disableLineHighlight = false,
		disableLineNumbers = false,
		disableLinks = false,
		disableRuler = false,
		glyphMargin = false,
		inDiffEditor = false,
		placeholder = "",
		quickSuggestions,
		readOnly = false,
		size = "md",
	}: EditorOptions,
): Monaco.editor.IStandaloneEditorConstructionOptions {
	const options = useMemo((): Monaco.editor.IStandaloneEditorConstructionOptions => {
		const [fontSize, topPadding, xPadding, bottomPadding = topPadding] = SIZE_TO_CONFIG[size];

		return {
			...DEFAULT_OPTIONS,
			readOnly,
			placeholder,
			lineNumbers: disableLineNumbers ? "off" : "on",
			folding: !disableLineNumbers,
			// Without the xPadding for inDiffEditor, the +/- symbols do not show
			lineDecorationsWidth: disableLineNumbers || appearance !== "code" || inDiffEditor ? xPadding : 0,
			renderLineHighlight: disableLineHighlight ? "none" : "line",
			overviewRulerLanes: disableRuler ? 0 : undefined,
			overviewRulerBorder: disableRuler ? false : undefined,
			quickSuggestions: quickSuggestions
				? {
						other: "on",
						comments: "on",
						strings: "on",
					}
				: undefined,
			fontSize,
			padding: {
				top: topPadding,
				bottom: bottomPadding,
			},
			links: !disableLinks,
			inDiffEditor,
			glyphMargin,
			// Custom appearance options
			...(appearance === "input" && INPUT_OPTIONS),
			...(appearance === "textarea" && TEXTAREA_OPTIONS),
			...(appearance === "code" && CODE_OPTIONS),
		};
	}, [
		appearance,
		disableLineHighlight,
		disableLineNumbers,
		disableLinks,
		disableRuler,
		glyphMargin,
		inDiffEditor,
		placeholder,
		quickSuggestions,
		readOnly,
		size,
	]);

	// update the options on change
	useEffect(() => {
		editor?.updateOptions(options);
	}, [editor, options]);

	return options;
}
